import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import APIServices from "../services/APIServices";
import Loader from "../components/Loader";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import appToast, { TOAST_TYPE } from "../utils/AppToast";
import {
  capitalizeFirstLetter,
  formatDateTime,
  getformattedPhoneNumber,
  PAGE_LIMIT,
  selectLoader,
} from "../utils/Helpers";
import { useAuth } from "../contexts/AuthContext";

const SubscribersList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subScribers, setSubScribers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Default to page 1
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSubscribers(currentPage);
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handler = setTimeout(() => {
      fetchSubscribers(currentPage, searchQuery);
    }, 300);

    // Cleanup function to clear the timeout if dependencies change before delay
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchSubscribers = async (page, searchQuery = "") => {
    setTableLoading(true);
    try {
      const response = await APIServices.get(
        `/admin/subscribers?page=${page}&limit=${PAGE_LIMIT}&search=${searchQuery}`
      );
      if (response) {
        setCurrentPage(response.currentPage);
        setTotalPages(response.totalPages);
        setSubScribers(response.data);
      }
    } catch (err) {
      setError("Failed to fetch blog categories.");
      console.error("Error fetching blog categories:", err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page && page !== currentPage) {
        fetchSubscribers(page);
      }
    },
    [currentPage]
  );

  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const filteredBlogs = subScribers || [];

  const handleEditClick = useCallback(
    (slug) => {
      navigate(`edit/${slug}`);
    },
    [navigate]
  );

  const handleToggle = async (userId, currentStatus) => {
    if (!isSuperAdmin) {
      appToast("Only super admins can change the status.", TOAST_TYPE.ERROR);
      return;
    }
    try {
      // Optimistically update the UI
      setSubScribers((prevList) =>
        prevList.map((user) =>
          user._id === userId ? { ...user, loginStatus: !currentStatus } : user
        )
      );

      // Make API call
      let response = await APIServices.put(
        `/admin/update-login-status/${userId}`,
        {
          loginStatus: !currentStatus,
        }
      );
      if (response.message) {
        appToast(response.message, TOAST_TYPE.SUCCESS);
      }
    } catch (error) {
      console.error("Error updating login status:", error);
      appToast("Failed to update login status.", TOAST_TYPE.ERROR);

      // Revert UI change on failure
      setSubScribers((prevList) =>
        prevList.map((user) =>
          user._id === userId ? { ...user, loginStatus: currentStatus } : user
        )
      );
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <section className="content adminUser eventtype">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card-top">
                    <div className="input-group">
                      <input
                        onChange={(e) => setSearchQuery(e.target.value)}
                        type="search"
                        className="form-control"
                        placeholder="Search Name"
                      />
                      <div className="input-group-append">
                        <i className="fa fa-search"></i>
                      </div>
                    </div>

                    {/* <div className="btn-group-right">
                      <Link to="/blogs/add">
                        {" "}
                        <button
                          type="submit"
                          className="btn btn-blue btn-block"
                        >
                          + ADD BLOG
                        </button>
                      </Link>
                    </div> */}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="table-responsive">
                    <table
                      id="example"
                      className="table table-striped table-bordered"
                      cellSpacing="0"
                      width="100%"
                    >
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th colSpan={4} style={{ width: "600px" }}>
                            Company name
                          </th>
                          <th>Email</th>
                          <th>Phone Number</th>
                          <th>Role</th>
                          <th>Current Plan</th>
                          <th>Last Active</th>
                          <th>Projects Active</th>
                          <th>Login Status</th>
                        </tr>
                      </thead>
                      <tbody
                        style={{
                          display: tableLoading
                            ? "table-caption"
                            : "table-row-group",
                        }}
                      >
                        {tableLoading ? (
                          selectLoader(50)
                        ) : filteredBlogs.length > 0 ? (
                          filteredBlogs?.map((org, index) => (
                            <tr key={org._id}>
                              <td>{org?.fullName}</td>
                              <td colSpan={4} style={{ width: "600px" }}>
                                {capitalizeFirstLetter(org.companyId.name)}
                              </td>
                              <td>{org?.email}</td>
                              <td>
                                {org?.countryDialCode +
                                  getformattedPhoneNumber(org?.phoneNumber)}
                              </td>
                              <td>{org?.userType?.role}</td>
                              <td>{org?.subscriptionPlanId?.planName}</td>
                              <td>{formatDateTime(org?.lastLoginTime)}</td>
                              <td>{org?.ongoingProjects}</td>
                              <td>
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={org.loginStatus}
                                    onChange={() =>
                                      handleToggle(org._id, org.loginStatus)
                                    }
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3">No data found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <ResponsivePagination
                      current={currentPage}
                      total={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscribersList;
